import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { batchesApi, suggestApi, certBranchesApi } from "../../../service/certificate"
import { getAllSessions } from "../../../service/sessions"

// Best Teacher — teacher-based (no student GR). Pick a session + campus, load
// teachers, tick the ones to award, set per-teacher designation/subject + a
// shared message, save as a batch. Mirrors RMS BestTeacherForm (functionality);
// buildContext reads teacher_full_name / teacher_designation / teacher_subject /
// teacher_title / teacher_email / teacher_phone / teacher_employee_id / message.

const DESIGNATIONS = ["Teacher", "Deputy Head", "Coordinator", "Principal"]
const asArray = (x: any): any[] => (Array.isArray(x) ? x : x?.data || x?.sessions || [])
const slug = (s: string) => String(s).trim().replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "")

const BestTeacherForm = ({ onSaved }: { onSaved?: () => void }) => {
  const [sessions, setSessions] = useState<any[]>([])
  const [campuses, setCampuses] = useState<any[]>([])
  const [teachers, setTeachers] = useState<any[]>([])
  const [loadingT, setLoadingT] = useState(false)

  const [sessionId, setSessionId] = useState("")
  const [campusId, setCampusId] = useState("")
  const [fileName, setFileName] = useState("")
  const [fileNameTouched, setFileNameTouched] = useState(false)
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const [details, setDetails] = useState<Record<string, { designation?: string; subject?: string }>>({})
  const [message, setMessage] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getAllSessions({ limit: 1000, page: 1 }).then(r => setSessions(asArray(r))).catch(() => {})
    certBranchesApi().then(r => setCampuses(r.data || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (fileNameTouched) return
    const sName = sessions.find(s => s._id === sessionId)?.title || sessions.find(s => s._id === sessionId)?.name || "Session"
    const cName = campusId ? campuses.find(c => c._id === campusId)?.name || "" : "All_Campuses"
    setFileName([slug(sName), slug(cName), "BestTeacher", new Date().toISOString().slice(0, 10)].filter(Boolean).join("_"))
  }, [sessionId, campusId, sessions, campuses, fileNameTouched])

  const loadTeachers = async () => {
    setLoadingT(true)
    try {
      const res = await suggestApi.teachers({})
      setTeachers(res.data || [])
      setPicked({})
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load teachers")
    } finally {
      setLoadingT(false)
    }
  }

  const keyOf = (t: any) => String(t.teacherKey || t.studentId || t.teacher_employee_id || t.teacher_full_name)
  const setDetail = (k: string, patch: { designation?: string; subject?: string }) =>
    setDetails(d => ({ ...d, [k]: { ...(d[k] || {}), ...patch } }))
  const pickedCount = useMemo(() => Object.values(picked).filter(Boolean).length, [picked])

  const save = async () => {
    if (!sessionId) return toast.error("Pick a session.")
    const chosen = teachers.filter(t => picked[keyOf(t)])
    if (!chosen.length) return toast.error("Pick at least one teacher.")
    setSaving(true)
    try {
      const studentsData = chosen.map(t => {
        const k = keyOf(t)
        const d = details[k] || {}
        return {
          teacher_full_name: t.teacher_full_name || t.name || "",
          teacher_email: t.teacher_email || "",
          teacher_phone: t.teacher_phone || "",
          teacher_employee_id: t.teacher_employee_id || "",
          teacher_title: t.teacher_title || "",
          teacher_designation: d.designation ?? t.teacher_designation ?? "",
          teacher_affiliation: t.teacher_affiliation || "",
          teacher_subject: d.subject ?? t.teacher_subject ?? "",
          message: message || "",
        }
      })
      const res = await batchesApi.create({ type: "best_teacher", fileName: fileName.trim(), session: sessionId, branch: campusId || undefined, studentsData })
      toast.success(`Saved ${chosen.length} teacher(s)${res.data?.refNumber ? ` — ${res.data.refNumber}` : ""}`)
      setPicked({})
      setFileNameTouched(false)
      onSaved?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-header"><h5 className="mb-0">1. Scope</h5></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Session *</label>
              <select className="form-select" value={sessionId} onChange={e => setSessionId(e.target.value)}>
                <option value="">Select…</option>
                {sessions.map(s => <option key={s._id} value={s._id}>{s.title || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Campus</label>
              <select className="form-select" value={campusId} onChange={e => setCampusId(e.target.value)}>
                <option value="">All Campuses</option>
                {campuses.map(c => <option key={c._id} value={c._id}>{c.campusNo ? `${c.campusNo} — ` : ""}{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button className="btn btn-outline-primary" onClick={loadTeachers} disabled={loadingT}>
                {loadingT ? "Loading…" : "Load Teachers"}
              </button>
            </div>
          </div>
          <div className="mt-3">
            <label className="form-label">Message (applies to all)</label>
            <input className="form-control" placeholder="e.g. for outstanding dedication this year" value={message} onChange={e => setMessage(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">2. Teachers ({pickedCount} selected)</h5>
          <div className="d-flex align-items-center gap-2">
            <input className="form-control form-control-sm" style={{ width: 320 }} placeholder="Batch file name"
              value={fileName} onChange={e => { setFileName(e.target.value); setFileNameTouched(true) }} />
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : `Save ${pickedCount || ""}`}</button>
          </div>
        </div>
        <div className="card-body table-responsive">
          {teachers.length === 0 ? (
            <p className="text-muted mb-0">Click <strong>Load Teachers</strong> to list staff, then tick the ones to award.</p>
          ) : (
            <table className="table table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Name</th>
                  <th style={{ width: 180 }}>Designation</th>
                  <th>Subject / Dept</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map(t => {
                  const k = keyOf(t)
                  return (
                    <tr key={k}>
                      <td><input type="checkbox" className="form-check-input" checked={!!picked[k]} onChange={e => setPicked(p => ({ ...p, [k]: e.target.checked }))} /></td>
                      <td>{t.teacher_full_name || t.name}{t.teacher_employee_id ? <small className="text-muted"> · {t.teacher_employee_id}</small> : null}</td>
                      <td>
                        <select className="form-select form-select-sm" value={details[k]?.designation ?? t.teacher_designation ?? ""} onChange={e => setDetail(k, { designation: e.target.value })}>
                          <option value="">—</option>
                          {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </td>
                      <td><input className="form-control form-control-sm" value={details[k]?.subject ?? t.teacher_subject ?? ""} onChange={e => setDetail(k, { subject: e.target.value })} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

export default BestTeacherForm
